import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    Typography,
    TextField,
    Button,
    IconButton,
    Stack,
    Divider,
    Checkbox,
    FormControlLabel,
    FormControl,
    InputAdornment,
    MenuItem,
    Paper,
    Tooltip,
    Collapse
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PercentIcon from '@mui/icons-material/Percent';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CalculateIcon from '@mui/icons-material/Calculate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import CashIcon from '@mui/icons-material/LocalAtm';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaidIcon from '@mui/icons-material/Paid';
import { useFormContext } from 'react-hook-form';
import { useResponsive } from '../../../../../utils/useResponsive';
import { AdditionalCharge, AmountSummarySectionProps, Discount, PaymentTypeOption } from '../../../types';

const AmountSummarySection: React.FC<AmountSummarySectionProps> = ({
    // Labels with defaults
    addChargeButtonText = 'Add Charge',
    addDiscountButtonText = 'Add Discount',
    taxableAmountLabel = 'Taxable Amount',
    discountLabel = 'Discount',
    discountTypeLabel = 'Type',
    discountAmountLabel = 'Amount',
    roundOffLabel = 'Auto Round Off',
    totalAmountLabel = 'Total Amount',
    balanceAmountLabel = 'Balance Due',
    chargesLabel = 'Additional Charges',
    receivedAmountLabel = 'Received Amount',
    paymentTypeLabel = 'Payment Type',
    fullyPaidLabel = 'Mark as Fully Paid',

    // Form field names with defaults
    formFieldNames = {
        charges: 'charges',
        discount: 'discount',
        roundOff: 'roundOff',
        totalAmount: 'totalAmount',
        taxableAmount: 'taxableAmount',
        subtotal: 'subtotal',
        receivedAmount: 'receivedAmount',
        paymentType: 'paymentType',
        balanceAmount: 'balanceAmount',
        fullyPaid: 'fullyPaid',
        taxBreakdown: 'taxBreakdown'
    },

    // Values
    taxableAmount = 0,
    totalAmount: propTotalAmount = 0, // Total from items including item-level taxes
    charges: propCharges = [],
    discount: propDiscount,
    roundOff: propRoundOff = false,
    receivedAmount: propReceivedAmount = 0,
    paymentType: propPaymentType = 'CASH',
    fullyPaid: propFullyPaid = false,

    // NEW: Tax breakdown props
    taxBreakdown = [{
        sgst: 0,
        cgst: 0,
        igst: 0
    }],

    // Callbacks
    onChargesChange,
    onDiscountChange,
    onRoundOffChange,
    onTotalAmountChange,
    onReceivedAmountChange,
    onPaymentTypeChange,
    onFullyPaidChange,

    // Configuration with defaults
    showCharges = true,
    showDiscount = true,
    showRoundOff = true,
    showTaxableAmount = true,
    showBalanceAmount = true,
    showReceivedAmount = true,
    maxCharges = 5,
    disabled = false,
    compact = false,
    showFullyPaid = true,
    showSubTotal = true,
    showTotalAmount = true,
    showDivider = true,

    // Calculation settings
    taxRate = 0, // Not used since tax is already in propTotalAmount
    autoRoundOff = true,
    roundOffDecimal = 1,

    // Discount options
    discountTypes = [
        { value: 'after_tax', label: 'After Tax' },
        { value: 'before_tax', label: 'Before Tax' }
    ],

    // Payment options
    paymentTypes = [
        { value: 'CASH', label: 'Cash', icon: <CashIcon /> },
        { value: 'UPI', label: 'UPI', icon: <PaymentIcon /> },
        { value: 'CARD', label: 'Card', icon: <CreditCardIcon /> },
        { value: 'BANK_TRANSFER', label: 'Bank Transfer', icon: <AccountBalanceIcon /> }
    ]
}) => {
    // State
    const [charges, setCharges] = useState<AdditionalCharge[]>(propCharges);
    const [discount, setDiscount] = useState<Discount | null>(propDiscount || null);
    const [isRoundOff, setIsRoundOff] = useState(propRoundOff);
    const [showDiscountSection, setShowDiscountSection] = useState(false);
    const [discountAmountInput, setDiscountAmountInput] = useState<string>('0');
    const [isDiscountPercentage, setIsDiscountPercentage] = useState(propDiscount?.isPercentage || false);
    const [receivedAmount, setReceivedAmount] = useState<number>(propReceivedAmount);
    const [paymentType, setPaymentType] = useState<string>(propPaymentType);
    const [isFullyPaid, setIsFullyPaid] = useState<boolean>(propFullyPaid);

    // Responsive hook
    const { isMobile, isMobileOrTablet } = useResponsive();
    const shouldBeCompact = compact || isMobileOrTablet;

    // React Hook Form integration
    const { setValue, watch } = useFormContext?.() || {};

    // Get form values if using react-hook-form
    const formCharges = formFieldNames.charges ? watch?.(formFieldNames.charges) : undefined;
    const formDiscount = formFieldNames.discount ? watch?.(formFieldNames.discount) : undefined;
    const formRoundOff = formFieldNames.roundOff ? watch?.(formFieldNames.roundOff) : undefined;
    const formReceivedAmount = formFieldNames.receivedAmount ? watch?.(formFieldNames.receivedAmount) : undefined;
    const formPaymentType = formFieldNames.paymentType ? watch?.(formFieldNames.paymentType) : undefined;
    const formFullyPaid = formFieldNames.fullyPaid ? watch?.(formFieldNames.fullyPaid) : undefined;

    // Use form values if available, otherwise use props
    const displayCharges = formCharges !== undefined ? formCharges : charges;
    const displayDiscount = formDiscount !== undefined ? formDiscount : discount;
    const displayRoundOff = formRoundOff !== undefined ? formRoundOff : isRoundOff;
    const displayReceivedAmount = formReceivedAmount !== undefined ? formReceivedAmount : receivedAmount;
    const displayPaymentType = formPaymentType !== undefined ? formPaymentType : paymentType;
    const displayFullyPaid = formFullyPaid !== undefined ? formFullyPaid : isFullyPaid;

    // Calculate charges total
    const calculateChargesTotal = (): number => {
        return displayCharges.reduce((sum: number, charge: AdditionalCharge) => sum + (charge.amount || 0), 0);
    };

    // Calculate discount amount based on propTotalAmount + charges
    const calculateDiscountAmount = (): number => {
        if (!displayDiscount || !displayDiscount.amount) return 0;

        const baseAmount = propTotalAmount + calculateChargesTotal();

        if (displayDiscount.isPercentage) {
            return (baseAmount * displayDiscount.amount) / 100;
        }
        return displayDiscount.amount;
    };

    // Calculate final total: propTotalAmount + charges - discount
    const calculateFinalTotal = (): number => {
        const chargesTotal = calculateChargesTotal();
        const discountAmount = calculateDiscountAmount();

        let total = propTotalAmount + chargesTotal - discountAmount;

        // Apply round off if enabled
        if (displayRoundOff && autoRoundOff && roundOffDecimal > 0) {
            total = Math.round(total / roundOffDecimal) * roundOffDecimal;
        }

        return Math.max(0, total);
    };

    // Calculate balance amount
    const calculateBalance = (): number => {
        const total = calculateFinalTotal();
        const received = displayFullyPaid ? total : (displayReceivedAmount || 0);
        return Math.max(0, total - received);
    };

    const finalTotal = calculateFinalTotal();
    const balance = calculateBalance();
    const chargesTotal = calculateChargesTotal();
    const discountAmountValue = calculateDiscountAmount();

    // Update form values when calculations change
    useEffect(() => {
        const safeTotal = isNaN(finalTotal) ? 0 : finalTotal;
        const safeBalance = isNaN(balance) ? 0 : balance;

        if (formFieldNames.totalAmount && setValue) {
            setValue(formFieldNames.totalAmount, safeTotal);
        }
        if (formFieldNames.taxableAmount && setValue) {
            setValue(formFieldNames.taxableAmount, taxableAmount || 0);
        }
        if (formFieldNames.subtotal && setValue) {
            setValue(formFieldNames.subtotal, propTotalAmount || 0);
        }
        if (formFieldNames.balanceAmount && setValue) {
            setValue(formFieldNames.balanceAmount, safeBalance);
        }

        const taxBreakdownField = (formFieldNames as any)?.taxBreakdown;
        if (taxBreakdownField && setValue && Array.isArray(taxBreakdown)) {
            setValue(taxBreakdownField, taxBreakdown);
        }

        if (onTotalAmountChange) {
            onTotalAmountChange(safeTotal);
        }
    }, [finalTotal, balance, taxableAmount, propTotalAmount, formFieldNames, setValue, onTotalAmountChange, taxBreakdown]);

    // Initialize discount amount input
    useEffect(() => {
        if (displayDiscount?.amount !== undefined) {
            setDiscountAmountInput(displayDiscount.amount.toString());
        }
    }, [displayDiscount]);

    // Handle fully paid change
    const handleFullyPaidChange = (checked: boolean) => {
        setIsFullyPaid(checked);

        // If fully paid, set received amount equal to total amount
        if (checked) {
            const totalAmount = calculateFinalTotal();
            setReceivedAmount(totalAmount);
            if (formFieldNames.receivedAmount && setValue) {
                setValue(formFieldNames.receivedAmount, totalAmount);
            }
            if (onReceivedAmountChange) {
                onReceivedAmountChange(totalAmount);
            }
        }

        if (formFieldNames.fullyPaid && setValue) {
            setValue(formFieldNames.fullyPaid, checked);
        }
        if (onFullyPaidChange) {
            onFullyPaidChange(checked);
        }
    };

    // Handle received amount change
    const handleReceivedAmountChange = (value: string) => {
        const amount = parseFloat(value) || 0;
        setReceivedAmount(amount);

        // If amount equals total, automatically check fully paid
        // if (amount >= calculateFinalTotal()) {
        //     handleFullyPaidChange(true);
        // } else if (displayFullyPaid) {
        //     handleFullyPaidChange(false);
        // }

        if (formFieldNames.receivedAmount && setValue) {
            setValue(formFieldNames.receivedAmount, amount);
        }
        if (onReceivedAmountChange) {
            onReceivedAmountChange(amount);
        }
    };

    // Handle add charge button click
    const handleAddChargeClick = () => {
        const newCharge: AdditionalCharge = {
            id: Date.now().toString(),
            label: `Charge ${displayCharges.length + 1}`,
            amount: 0
        };

        const updatedCharges = [...displayCharges, newCharge];
        updateCharges(updatedCharges);
    };

    // Update charge
    const updateCharge = (id: string, field: 'label' | 'amount', value: string | number) => {
        const updatedCharges = displayCharges.map((charge: AdditionalCharge) => {
            if (charge.id === id) {
                return {
                    ...charge,
                    [field]: field === 'amount' ? (Number(value) || 0) : value
                };
            }
            return charge;
        });
        updateCharges(updatedCharges);
    };

    // Remove charge
    const removeCharge = (id: string) => {
        const updatedCharges = displayCharges.filter((charge: AdditionalCharge) => charge.id !== id);
        updateCharges(updatedCharges);
    };

    // Update charges
    const updateCharges = (updatedCharges: AdditionalCharge[]) => {
        setCharges(updatedCharges);
        if (formFieldNames.charges && setValue) {
            setValue(formFieldNames.charges, updatedCharges);
        }
        if (onChargesChange) {
            onChargesChange(updatedCharges);
        }
    };

    // Handle discount type change
    const handleDiscountTypeChange = (type: 'before_tax' | 'after_tax') => {
        if (discount) {
            const updatedDiscount = { ...discount, type };
            updateDiscount(updatedDiscount);
        }
    };

    // Handle discount amount change
    const handleDiscountAmountChange = (value: string) => {
        setDiscountAmountInput(value);
        const numValue = parseFloat(value) || 0;
        if (discount) {
            const updatedDiscount = { ...discount, amount: numValue };
            updateDiscount(updatedDiscount);
        }
    };

    // Handle discount percentage toggle
    const handleDiscountPercentageToggle = () => {
        const newIsPercentage = !isDiscountPercentage;
        setIsDiscountPercentage(newIsPercentage);
        if (discount) {
            const updatedDiscount = { ...discount, isPercentage: newIsPercentage };
            updateDiscount(updatedDiscount);
        }
    };

    // Update discount
    const updateDiscount = (updatedDiscount: Discount) => {
        setDiscount(updatedDiscount);
        if (formFieldNames.discount && setValue) {
            setValue(formFieldNames.discount, updatedDiscount);
        }
        if (onDiscountChange) {
            onDiscountChange(updatedDiscount);
        }
    };

    // Remove discount
    const handleRemoveDiscount = () => {
        setDiscount(null);
        setShowDiscountSection(false);
        setDiscountAmountInput('0');
        setIsDiscountPercentage(false);
        if (formFieldNames.discount && setValue) {
            setValue(formFieldNames.discount, null);
        }
        if (onDiscountChange) {
            onDiscountChange(null);
        }
    };

    // Handle add discount click
    const handleAddDiscountClick = () => {
        if (!displayDiscount) {
            setDiscount({
                type: 'before_tax',
                amount: 0,
                isPercentage: false
            });
        }
        setShowDiscountSection(true);
    };

    // Handle round off change
    const handleRoundOffChange = (checked: boolean) => {
        setIsRoundOff(checked);
        if (formFieldNames.roundOff && setValue) {
            setValue(formFieldNames.roundOff, checked);
        }
        if (onRoundOffChange) {
            onRoundOffChange(checked);
        }
    };

    // Handle payment type change
    const handlePaymentTypeChange = (type: string) => {
        setPaymentType(type);
        if (formFieldNames.paymentType && setValue) {
            setValue(formFieldNames.paymentType, type);
        }
        if (onPaymentTypeChange) {
            onPaymentTypeChange(type);
        }
    };

    // Format currency
    const formatCurrency = (amount: number): string => {
        if (isNaN(amount)) return '₹0.00';

        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    // Calculate tax breakdown display (SGST/CGST/IGST)
    const renderTaxBreakdown = () => {
        // Check if taxBreakdown is an array and has items
        if (!Array.isArray(taxBreakdown)) {
            return null;
        }

        // Filter out items with 0% tax rate
        const validTaxItems = taxBreakdown.filter(item =>
            (item.sgst && item.sgst > 0) ||
            (item.cgst && item.cgst > 0) ||
            (item.igst && item.igst > 0)
        );

        if (validTaxItems.length === 0) {
            return null;
        }

        return (
            <Box sx={{ mt: 0.5, height: "100%" }}>
                {validTaxItems.map((taxItem, index) => {
                    // Calculate amounts for each tax type
                    const sgstAmount = taxItem.sgst ? (taxableAmount * taxItem.sgst) / 100 : 0;
                    const cgstAmount = taxItem.cgst ? (taxableAmount * taxItem.cgst) / 100 : 0;
                    // const igstAmount = taxItem.igst ? (taxableAmount * taxItem.igst) / 100 : 0;

                    return (
                        <React.Fragment key={index}>
                            {/* SGST Display */}
                            {taxItem.sgst && taxItem.sgst > 0 && (
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mt: 0.25
                                }}>
                                    <Typography variant="caption" color="text.secondary">
                                        SGST ({taxItem.sgst}%)
                                    </Typography>
                                    <Typography variant="caption" fontWeight="medium">
                                        {formatCurrency(sgstAmount)}
                                    </Typography>
                                </Box>
                            )}

                            {/* CGST Display */}
                            {taxItem.cgst && taxItem.cgst > 0 && (
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mt: 0.25
                                }}>
                                    <Typography variant="caption" color="text.secondary">
                                        CGST ({taxItem.cgst}%)
                                    </Typography>
                                    <Typography variant="caption" fontWeight="medium">
                                        {formatCurrency(cgstAmount)}
                                    </Typography>
                                </Box>
                            )}

                            {/* IGST Display
                            {taxItem.igst && taxItem.igst > 0 && (
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mt: 0.25
                                }}>
                                    <Typography variant="caption" color="text.secondary">
                                        IGST ({taxItem.igst}%)
                                    </Typography>
                                    <Typography variant="caption" fontWeight="medium">
                                        {formatCurrency(igstAmount)}
                                    </Typography>
                                </Box>
                            )} */}
                        </React.Fragment>
                    );
                })}
            </Box>
        );
    };

    return (
        <Card sx={{
            p: shouldBeCompact ? 1.5 : 2,
            borderRadius: 1,
            width: '100%',
            '& .MuiTextField-root': {
                mb: shouldBeCompact ? 0.5 : 1
            }
        }}>
            <Stack spacing={shouldBeCompact ? 1.5 : 2}>
                {/* Taxable Amount - from props */}
                {showTaxableAmount && (
                    <Box>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Typography
                                variant={shouldBeCompact ? "caption" : "body2"}
                                color="text.secondary"
                                fontWeight="medium"
                            >
                                {taxableAmountLabel}
                            </Typography>
                            <Typography
                                variant={shouldBeCompact ? "body2" : "body1"}
                                fontWeight="bold"
                                color="primary"
                            >
                                {formatCurrency(taxableAmount)}
                            </Typography>
                        </Box>

                        {/* Tax breakdown display */}
                        {renderTaxBreakdown()}
                    </Box>
                )}

                {/* Additional Charges */}
                {showCharges && (
                    <Box>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 0.5
                        }}>
                            <Typography
                                variant={shouldBeCompact ? "caption" : "body2"}
                                color="text.secondary"
                                fontWeight="medium"
                            >
                                {chargesLabel}
                            </Typography>

                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleAddChargeClick}
                                // disabled={disabled || displayCharges.length >= maxCharges}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                                    py: 0.25,
                                    px: 1
                                }}
                            >
                                {addChargeButtonText}
                            </Button>
                        </Box>

                        {/* Existing Charges */}
                        {displayCharges.length > 0 && (
                            <Stack spacing={0.5} sx={{ mb: 1 }}>
                                {displayCharges.map((charge: AdditionalCharge) => (
                                    <Box
                                        key={charge.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            p: 0.75,
                                            bgcolor: 'grey.50',
                                            borderRadius: 0.5
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                            <TextField
                                                size="small"
                                                value={charge.label}
                                                onChange={(e) => updateCharge(charge.id, 'label', e.target.value)}
                                                // disabled={disabled}
                                                sx={{ flex: 2 }}
                                                InputProps={{
                                                    sx: { fontSize: '0.875rem' }
                                                }}
                                            />
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={charge.amount}
                                                onChange={(e) => updateCharge(charge.id, 'amount', e.target.value)}
                                                // disabled={disabled}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            ₹
                                                        </InputAdornment>
                                                    ),
                                                    sx: { fontSize: '0.875rem' }
                                                }}
                                                sx={{ flex: 1 }}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={() => removeCharge(charge.id)}
                                                // disabled={disabled}
                                                color="error"
                                                sx={{ ml: 0.5 }}
                                            >
                                                <RemoveIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        )}

                        {/* Charges Total */}
                        {displayCharges.length > 0 && (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mt: 0.5
                            }}>
                                <Typography variant="caption" color="text.secondary">
                                    Charges Total
                                </Typography>
                                <Typography variant="caption" fontWeight="medium">
                                    {formatCurrency(chargesTotal)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Subtotal - shows propTotalAmount (already includes item taxes) */}
                {showSubTotal && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: shouldBeCompact ? 1 : 1.5,
                        bgcolor: 'primary.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'primary.100'
                    }}>
                        <Typography
                            variant={shouldBeCompact ? "body2" : "body1"}
                            fontWeight="bold"
                            color="primary"
                        >
                            Subtotal
                        </Typography>
                        <Typography
                            variant={shouldBeCompact ? "body2" : "body1"}
                            fontWeight="bold"
                            color="primary"
                        >
                            {formatCurrency(propTotalAmount)}
                        </Typography>
                    </Box>
                )}


                {/* Discount Section */}
                {showDiscount && (
                    <Box>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 0.5
                        }}>
                            <Typography
                                variant={shouldBeCompact ? "caption" : "body2"}
                                color="text.secondary"
                                fontWeight="medium"
                            >
                                {discountLabel}
                            </Typography>

                            {!showDiscountSection && (
                                <Button
                                    size="small"
                                    startIcon={<LocalOfferIcon />}
                                    onClick={handleAddDiscountClick}
                                    // disabled={disabled}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                                        py: 0.25,
                                        px: 1
                                    }}
                                >
                                    {addDiscountButtonText}
                                </Button>
                            )}
                        </Box>

                        {/* Discount Section - Collapsible */}
                        <Collapse in={showDiscountSection}>
                            <Paper sx={{ p: 1.5, mb: 1, bgcolor: 'grey.50' }}>
                                <Stack spacing={1} direction={"row"}>
                                    <FormControl fullWidth size="small">
                                        <Typography variant="caption" sx={{ mb: 0.5 }}>
                                            {/* {discountTypeLabel} */}
                                        </Typography>
                                        <TextField
                                            select
                                            size="small"
                                            label={discountTypeLabel}
                                            value={displayDiscount?.type || 'after_tax'}
                                            onChange={(e) => handleDiscountTypeChange(e.target.value as 'after_tax' | 'before_tax'  )}
                                            // disabled={disabled}
                                        >
                                            {discountTypes.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </FormControl>

                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            value={discountAmountInput}
                                            onChange={(e) => handleDiscountAmountChange(e.target.value)}
                                            // disabled={disabled}
                                            label={discountAmountLabel}
                                            InputProps={{
                                                startAdornment: isDiscountPercentage ? (
                                                    <InputAdornment position="start">
                                                        <PercentIcon fontSize="small" />
                                                    </InputAdornment>
                                                ) : (
                                                    <InputAdornment position="start">
                                                        ₹
                                                    </InputAdornment>
                                                )
                                            }}
                                        />

                                        <Tooltip title={isDiscountPercentage ? "Switch to Amount" : "Switch to Percentage"}>
                                            <IconButton
                                                size="small"
                                                onClick={handleDiscountPercentageToggle}
                                                // disabled={disabled}
                                                color={isDiscountPercentage ? "primary" : "default"}
                                            >
                                                {isDiscountPercentage ? <PercentIcon fontSize="small" /> : <AttachMoneyIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Stack>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Button
                                        size="small"
                                        color="error"
                                        onClick={handleRemoveDiscount}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Remove Discount
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => setShowDiscountSection(false)}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Done
                                    </Button>
                                </Box>
                            </Paper>
                        </Collapse>

                        {/* Applied Discount Display */}
                        {displayDiscount && displayDiscount.amount > 0 && !showDiscountSection && (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                p: 1,
                                bgcolor: 'warning.50',
                                borderRadius: 0.5,
                                mb: 0.5
                            }}>
                                <Box>
                                    <Typography variant="caption" display="block">
                                        {displayDiscount.type === 'before_tax' ? 'Discount Before Tax' : 'Discount After Tax'}
                                    </Typography>
                                    <Typography variant="body2">
                                        {displayDiscount.amount} {displayDiscount.isPercentage ? '%' : '₹'}
                                    </Typography>
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={() => setShowDiscountSection(true)}
                                    // disabled={disabled}
                                >
                                    <LocalOfferIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        )}

                        {/* Discount Amount Display */}
                        {displayDiscount && displayDiscount.amount > 0 && (
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mt: 0.5
                            }}>
                                <Typography variant="caption" color="text.secondary">
                                    Discount Amount
                                </Typography>
                                <Typography variant="caption" fontWeight="medium" color="error">
                                    - {formatCurrency(discountAmountValue)}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Round Off */}
                {showRoundOff && (
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={displayRoundOff}
                                    onChange={(e) => handleRoundOffChange(e.target.checked)}
                                    // disabled={disabled}
                                    size={shouldBeCompact ? "small" : "medium"}
                                    icon={<CalculateIcon />}
                                    checkedIcon={<CheckCircleIcon />}
                                />
                            }
                            label={
                                <Typography variant={shouldBeCompact ? "body2" : "body1"}>
                                    {roundOffLabel}
                                </Typography>
                            }
                        />
                    </Box>
                )}

                <Divider />

                {/* Total Amount - calculated final total */}
                {showTotalAmount && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        p: 2,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: 1
                    }}>
                        <Typography
                            variant={shouldBeCompact ? "body2" : "body1"}
                            fontWeight="bold"
                        >
                            {totalAmountLabel}
                        </Typography>
                        <Typography
                            variant={shouldBeCompact ? "h6" : "h5"}
                            fontWeight="bold"
                        >
                            {formatCurrency(finalTotal)}
                        </Typography>
                    </Box>
                )}

                {showDivider && (
                    <Divider />
                )}

                {/* Received Amount Section */}
                {showReceivedAmount && (
                    <Box>
                        <Typography
                            variant={shouldBeCompact ? "body2" : "body1"}
                            fontWeight="medium"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                        >
                            {receivedAmountLabel}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                            <TextField
                                size="small"
                                type="number"
                                value={displayReceivedAmount || 0}
                                onChange={(e) => handleReceivedAmountChange(e.target.value)}
                                disabled={disabled || displayFullyPaid}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            ₹
                                        </InputAdornment>
                                    )
                                }}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                select
                                size="small"
                                value={displayPaymentType || 'CASH'}
                                onChange={(e) => handlePaymentTypeChange(e.target.value)}
                                disabled={disabled}
                                sx={{ width: shouldBeCompact ? 180 : 200 }}
                            >
                                {paymentTypes.map((type: PaymentTypeOption) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {type.icon}
                                            <Typography variant="body2">
                                                {type.label}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>

                        {/* Fully Paid Checkbox */}
                        {showFullyPaid && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={displayFullyPaid}
                                        onChange={(e) => handleFullyPaidChange(e.target.checked)}
                                        // disabled={disabled}
                                        size="small"
                                        icon={<PaidIcon />}
                                        checkedIcon={<PaidIcon />}
                                    />
                                }
                                label={
                                    <Typography variant={shouldBeCompact ? "body2" : "body1"}>
                                        {fullyPaidLabel}
                                    </Typography>
                                }
                            />
                        )}

                    </Box>
                )}

                {showDivider && (
                    <Divider />
                )}

                {/* Balance Amount */}
                {showBalanceAmount && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1,
                        bgcolor: balance === 0 ? 'success.50' : 'warning.50',
                        borderRadius: 1
                    }}>
                        <Typography
                            variant={shouldBeCompact ? "body2" : "body1"}
                            fontWeight="medium"
                        >
                            {balanceAmountLabel}
                        </Typography>
                        <Typography
                            variant={shouldBeCompact ? "body2" : "body1"}
                            fontWeight="bold"
                            color={balance === 0 ? "success.main" : "warning.main"}
                        >
                            {formatCurrency(balance)}
                        </Typography>
                    </Box>
                )}
            </Stack>
        </Card>
    );
};

export default AmountSummarySection;